import { create } from 'zustand';

export type EntityChange = {
    readonly changedAt: number;
    readonly version: number | null;
};

type RecordableEntityChange = {
    readonly entityId: string;
    readonly version: number | null;
};

type EntityChangeState = {
    /** Latest agent-caused change per entity id, pruned shortly after arrival. */
    readonly changes: ReadonlyMap<string, EntityChange>;
    /** Event ids already recorded, so a re-mounted envelope cannot replay a glimmer. */
    readonly recordedEventIds: ReadonlySet<string>;
    readonly recordEntityChanges: (eventId: string, entityChanges: readonly RecordableEntityChange[]) => void;
};

// Entries only exist to trigger glimmers on already-mounted components, so they
// can be dropped aggressively; this just keeps the map from growing all session.
const PRUNE_AFTER_MS = 30_000;

export const useEntityChangeStore = create<EntityChangeState>((set) => ({
    changes: new Map(),
    recordedEventIds: new Set(),
    recordEntityChanges: (eventId, entityChanges) =>
        set((state) => {
            if (state.recordedEventIds.has(eventId) || entityChanges.length === 0) {
                return state;
            }
            const now = Date.now();
            const changes = new Map([...state.changes].filter(([, change]) => now - change.changedAt < PRUNE_AFTER_MS));
            for (const { entityId, version } of entityChanges) {
                changes.set(entityId, { changedAt: now, version });
            }
            return {
                changes,
                recordedEventIds: new Set([...state.recordedEventIds, eventId]),
            };
        }),
}));

export const recordEntityChanges = (eventId: string, entityChanges: readonly RecordableEntityChange[]): void => {
    useEntityChangeStore.getState().recordEntityChanges(eventId, entityChanges);
};
